import React, { useState, useMemo, useEffect } from 'react';
import FormSetup from '../Forms/FormSetup';
import { MasterConfigs } from '../../interfaces/entity';
import { constants } from '../../constants/common';
import ProfileCard from '../ContactInfo/ProfileCard';
import { useDispatch, useSelector } from 'react-redux';
import { postData } from '../../actions/developer';

interface LogCallModalProps {
    open: boolean;
    onCancel: () => void;
    onSuccess?: () => void;
    recordId: string;
    modelName: string;
    initialValues?: any;
}

const LogCallModal: React.FC<LogCallModalProps> = ({
    open,
    onCancel,
    onSuccess,
    recordId,
    modelName,
    initialValues
}) => {
    const [formInstance, setFormInstance] = useState<any>(null);
    const dispatch = useDispatch();
    const currentUser = useSelector((state: any) => state?.app?.user);

    // Map model names to refType
    const getRefType = (modelName: string): string => {
        const mapping: { [key: string]: string } = {
            'leads': 'Leads',
            'contacts': 'Contacts',
            'accounts': 'Businesses',
            'deals': 'Deals',
        };
        return mapping[modelName.toLowerCase()] || modelName;
    };

    const mappedRefType = useMemo(() => getRefType(modelName), [modelName]);

    const config: MasterConfigs = useMemo(() => ({
        api: () => `/call-logs`,
        title: "Log Call",
        modalTitle: () => 'Log Call',
        modalProps: {
            maskClosable: false,
        },
        getFormColumns: () => {
            const formColumns: any[] = [
                {
                    label: 'Call Owner',
                    name: 'callOwner',
                    type: 'select',
                    isCombo: true,
                    rules: [{ required: true, message: 'Call Owner is required' }],
                    commonProps: {
                        initialValue: currentUser?._id || undefined
                    },
                    extraOptions: currentUser?._id ? [{
                        value: currentUser._id,
                        name: constants.GET_FULL_NAME(currentUser) || 'Current User',
                        label: constants.GET_FULL_NAME(currentUser) || 'Current User'
                    }] : []
                },
                {
                    span: 16,
                },
                {
                    label: "Subject",
                    name: "subject",
                    type: "text",
                    span: 16,
                    rules: [{ required: true, message: 'Subject is required' }]
                },
                {
                    label: "Phone Number",
                    name: "phoneNumber",
                    type: "text",
                    span: 16,
                    rules: [{ required: true, message: 'Phone Number is required' }]
                },
                {
                    label: "Call Type",
                    name: "callType",
                    type: "select",
                    children: [
                        { name: 'Inbound', value: 'Inbound' },
                        { name: 'Outbound', value: 'Outbound' }
                    ],
                    commonProps: {
                        initialValue: 'Outbound'
                    }
                },
                {
                    label: "Call Date",
                    name: "callDate",
                    type: "date",
                    rules: [{ required: true, message: 'Call Date is required' }],
                    commonProps: {
                        initialValue: new Date()
                    }
                },
                {
                    label: "Call Status",
                    name: "callStatus",
                    type: "select",
                    children: [
                        { name: 'Completed', value: 'Completed' },
                        { name: 'No Answer', value: 'No Answer' },
                        { name: 'Busy', value: 'Busy' },
                        { name: 'Failed', value: 'Failed' },
                        { name: 'Missed', value: 'Missed' }
                    ],
                    commonProps: {
                        initialValue: 'Completed'
                    }
                },
                {
                    label: "Call Duration (seconds)",
                    name: "callDuration",
                    type: "number",
                    commonProps: {
                        initialValue: 0
                    }
                },
                {
                    span: 16,
                },
                {
                    label: "Description",
                    name: "description",
                    type: "textarea",
                    span: 16
                }
            ];

            return formColumns;
        },
        combo: [
            {
                api: '/users',
                name: 'callOwner',
                idKey: '_id',
                render: (text) => <ProfileCard
                    size={30}
                    name={constants.GET_FULL_NAME(text)}
                    avatarUrl={text?.avatar ? process.env.FILE_URL + text?.avatar : null}
                    role={null}
                />
            }
        ],
        onSubmit: async (id: string | null, values: any) => {
            const callData: any = {
                ...values,
                refType: mappedRefType,
                refId: recordId,
            };

            const apiUrl = `/call-logs`;
            const resData = await dispatch(postData(apiUrl, callData));

            if (resData && resData.code === 200) {
                if (onSuccess) {
                    onSuccess();
                }
            }

            return resData;
        },
        onError: (error: any) => {
            console.error('Error creating call log:', error);
        },
        afterSubmit: () => {
            onCancel();
        }
    }), [recordId, modelName, mappedRefType, formInstance, dispatch, currentUser]);

    // Set initial form values when form instance is available
    useEffect(() => {
        if (formInstance && mappedRefType) {
            formInstance.setFieldsValue({
                refType: mappedRefType,
                refId: recordId,
                callOwner: currentUser?._id || initialValues?.callOwner,
                ...initialValues
            });
        }
    }, [formInstance, mappedRefType, recordId, initialValues, currentUser]);

    return (
        <FormSetup
            config={config}
            open={open}
            onClose={onCancel}
            isModal={true}
            form={(form) => {
                setFormInstance(form);
            }}
        />
    );
};

export default LogCallModal;
